import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useEntityComments, useCommentsOrganizationId } from './useEntityComments';

/**
 * A threaded comment thread for a single Shape Up entity (scope / bet / pitch).
 *
 * Shape Up entities that back a Jira issue (scope, bet) sync their top-level
 * comments to Jira; the sync chip and "via Jira" badge surface that state. Pitch
 * threads are app-only and never show sync chrome. Replies are always app-only
 * (Jira comments are flat), so they carry no sync affordances.
 *
 * All data + realtime lives in useEntityComments; this component is presentation
 * plus the small amount of local edit/reply draft state.
 */
/**
 * Collapsible "Comments" panel for one entity. Loads lazily: the underlying
 * hook (which fetches over REST and opens a socket subscription) is only given a
 * real id once expanded, so a page listing many bets or pitches does not fire a
 * request per card until the reader opens one.
 */
function CommentThread({
  commentableType,
  commentableId,
  organizationId,
  defaultExpanded = false,
}) {
  const { user } = useAuth();
  const ctxOrganizationId = useCommentsOrganizationId();
  const orgId = organizationId || ctxOrganizationId;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const {
    comments,
    count,
    loading,
    error,
    create,
    edit,
    remove,
  } = useEntityComments(commentableType, expanded ? commentableId : null, orgId);

  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [actionError, setActionError] = useState(null);

  const isJiraBacked = commentableType !== 'pitch';

  const submitTop = async () => {
    const content = draft.trim();
    if (!content) return;
    setPosting(true);
    setActionError(null);
    try {
      await create(content, null);
      setDraft('');
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Box>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          cursor: 'pointer',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <ChatBubbleOutlineIcon fontSize="small" />
        <Typography variant="subtitle2">
          {expanded ? `Comments${count ? ` (${count})` : ''}` : 'Comments'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {actionError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {loading && comments.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box>
          {comments.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No comments yet.
            </Typography>
          )}
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={user?.id}
              isJiraBacked={isJiraBacked}
              onEdit={edit}
              onDelete={remove}
              onReply={(content) => create(content, comment.id)}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={6}
          placeholder="Add a comment…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submitTop();
            }
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={submitTop}
          disabled={posting || !draft.trim()}
          sx={{ mt: 0.25 }}
        >
          {posting ? <CircularProgress size={18} color="inherit" /> : 'Post'}
        </Button>
      </Box>
          {isJiraBacked && (
            <Typography variant="caption" color="text.secondary">
              Top-level comments sync to Jira. Replies stay in this app.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * One comment plus its (app-only) replies. Handles inline edit and a reply
 * composer. `isReply` suppresses the reply affordance and sync chrome, since a
 * reply is never itself Jira-backed.
 */
function CommentItem({
  comment,
  currentUserId,
  isJiraBacked,
  onEdit,
  onDelete,
  onReply,
  isReply = false,
}) {
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(comment.content);
  const [replying, setReplying] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const isAuthor = !!currentUserId && comment.userId === currentUserId;
  const fromJira = comment.source === 'jira';
  // Jira owns comments it sourced: only the app-side author can edit, and never
  // a Jira-sourced one (the server rejects it anyway).
  const canEdit = isAuthor && !fromJira;
  const authorName = comment.author?.name || comment.jiraAuthor || 'Unknown';

  const saveEdit = async () => {
    const content = editDraft.trim();
    if (!content) return;
    setBusy(true);
    try {
      await onEdit(comment.id, content);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async () => {
    const content = replyDraft.trim();
    if (!content) return;
    setBusy(true);
    try {
      await onReply(content);
      setReplyDraft('');
      setReplying(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, py: 1, pl: isReply ? 4 : 0 }}>
      <Avatar sx={{ width: 28, height: 28, fontSize: 14 }}>
        {authorName.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">{authorName}</Typography>
          {fromJira && (
            <Chip size="small" variant="outlined" label="via Jira" sx={{ height: 18 }} />
          )}
          {comment.createdAt && (
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </Typography>
          )}
          {comment.isEdited && (
            <Typography variant="caption" color="text.secondary">(edited)</Typography>
          )}
          {!isReply && isJiraBacked && !fromJira && (
            <SyncChip status={comment.jiraSyncStatus} error={comment.jiraSyncError} />
          )}
        </Box>

        {editing ? (
          <Box sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={6}
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Button size="small" onClick={saveEdit} disabled={busy || !editDraft.trim()}>
                Save
              </Button>
              <Button size="small" color="inherit" onClick={() => { setEditing(false); setEditDraft(comment.content); }}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
            {comment.content}
          </Typography>
        )}

        {!editing && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
            {!isReply && (
              <Tooltip title="Reply">
                <IconButton size="small" onClick={() => setReplying((v) => !v)}>
                  <ReplyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => { setEditing(true); setEditDraft(comment.content); }}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {isAuthor && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(comment.id)}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {replying && (
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder="Reply…"
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
            />
            <Button size="small" onClick={submitReply} disabled={busy || !replyDraft.trim()}>
              Reply
            </Button>
          </Box>
        )}

        {comment.replies?.map((reply) => (
          <CommentItem
            key={reply.id}
            comment={reply}
            currentUserId={currentUserId}
            isJiraBacked={isJiraBacked}
            onEdit={onEdit}
            onDelete={onDelete}
            onReply={onReply}
            isReply
          />
        ))}
      </Box>
    </Box>
  );
}

/** Small status chip reflecting where a top-level comment stands with Jira. */
function SyncChip({ status, error }) {
  if (status === 'synced') {
    return (
      <Tooltip title="Synced to Jira">
        <SyncIcon fontSize="small" color="success" />
      </Tooltip>
    );
  }
  if (status === 'pending') {
    return (
      <Tooltip title="Waiting to sync to Jira (connect Jira to push)">
        <CloudQueueIcon fontSize="small" color="disabled" />
      </Tooltip>
    );
  }
  if (status === 'failed') {
    return (
      <Tooltip title={error || 'Failed to sync to Jira'}>
        <SyncProblemIcon fontSize="small" color="error" />
      </Tooltip>
    );
  }
  return null;
}

export default CommentThread;
